import { useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';

/**
 * usePermissions - Single hook for auth state AND permission checks.
 *
 * ==================================================================================
 * WHEN TO USE THIS HOOK:
 * ==================================================================================
 * Use this hook for COMPONENT-LEVEL conditional rendering:
 * - Hiding/showing specific buttons or UI elements within a component
 * - Gating individual actions or features
 * - Fine-grained permission checks
 * - Navigation items in sidebars/menus
 *
 * Examples:
 *    Hide "New Project" button for users without ADMIN permission
 *    Hide "Settings" nav item for users without business permissions
 *    Disable edit button if user lacks DEFICIENCY_WRITE
 *    Show different content based on permissions
 *
 * ==================================================================================
 * WHEN NOT TO USE THIS HOOK:
 * ==================================================================================
 * DON'T use for protecting entire routes - use RequirePermission component instead
 * DON'T use if you need to redirect users - use RequirePermission component instead
 *
 * For route-level protection, see: src/components/RequirePermission.jsx
 * ==================================================================================
 *
 * Provides:
 * - Auth state (isLoading, isAuthenticated, userId, email, businessId)
 * - Business permission checks (canBusiness, canAnyBusiness, canAllBusiness)
 * - Project permission checks (canProject, canAnyProject, canAllProject)
 * - Role getter for workflow policy (getProjectRole - use only in deficiency quick actions)
 *
 * Usage Examples:
 *   const { isLoading, isAuthenticated, canBusiness, canProject } = usePermissions();
 *
 *   // Hide button while loading (Golden Rule - no flash of forbidden UI)
 *   if (isLoading) return <Skeleton width={100} height={36} />;
 *
 *   // Check single business permission
 *   if (canBusiness('ADMIN')) { ... }
 *
 *   // Check if user has ANY of multiple business permissions
 *   if (canAnyBusiness(['USER_READ', 'USER_WRITE'])) { ... }
 *
 *   // Check if user has ALL of multiple business permissions
 *   if (canAllBusiness(['ADMIN', 'USER_WRITE'])) { ... }
 *
 *   // Check single project permission
 *   if (canProject(projectId, 'DEFICIENCY_WRITE')) { ... }
 *
 *   // Check if user has ANY of multiple project permissions
 *   if (canAnyProject(projectId, ['DEFICIENCY_READ', 'DEFICIENCY_WRITE'])) { ... }
 */
export function usePermissions() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('usePermissions must be used within an AuthProvider');
    }

    const {
        userId,
        email,
        businessId,
        businessPermissions,
        projectAccess,
        isLoading,
        isAuthenticated,
        error,
        refreshPermissions,
        logout,
    } = context;

    // Derive Set from business permissions array for O(1) lookups
    const businessPermsSet = useMemo(() => new Set(businessPermissions), [businessPermissions]);

    // Derive Sets from project permissions for O(1) lookups
    const projectPermsSets = useMemo(() => {
        const result = {};
        for (const [projectId, access] of Object.entries(projectAccess)) {
            result[projectId] = new Set(access.permissions || []);
        }
        return result;
    }, [projectAccess]);

    return {
        // Auth state
        isLoading,
        isAuthenticated,
        userId,
        email,
        businessId,
        error,

        // Actions
        refreshPermissions,
        logout,

        // Business permission checks
        canBusiness: (permission) => businessPermsSet.has(permission),
        canAnyBusiness: (perms) => perms.some((p) => businessPermsSet.has(p)),
        canAllBusiness: (perms) => perms.every((p) => businessPermsSet.has(p)),

        // Project permission checks
        canProject: (projectId, permission) => {
            const perms = projectPermsSets[projectId];
            return perms ? perms.has(permission) : false;
        },
        canAnyProject: (projectId, perms) => {
            const projectPerms = projectPermsSets[projectId];
            return projectPerms ? perms.some((p) => projectPerms.has(p)) : false;
        },
        canAllProject: (projectId, perms) => {
            const projectPerms = projectPermsSets[projectId];
            return projectPerms ? perms.every((p) => projectPerms.has(p)) : false;
        },

        // Role getter for workflow policy ONLY (not for general gating)
        // Use this only in the centralized quick-action policy module
        getProjectRole: (projectId) => projectAccess[projectId]?.role || null,
    };
}

export default usePermissions;
